import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useApiQuery } from './useApi';

export interface Vote {
  id: string;
  code: string;
  name: string;
  type: string;
}

export interface FiscalYear {
  id: string;
  year: number;
  status: 'PLANNING' | 'OPEN' | 'CLOSING' | 'CLOSED';
}

export interface Combination {
  id: string;
  voteId: string;
  active: boolean;
  vote?: Vote;
  programme?: { id: string; code: string; name: string };
  subProgramme?: { id: string; code: string; name: string };
  econItem?: { id: string; gfsCode: string; name: string; class: string };
  fundSource?: { id: string; code: string; name: string; type: string };
}

export function comboLabel(c: Combination): string {
  const parts = [
    c.vote?.code,
    c.programme?.code,
    c.subProgramme?.code,
    c.econItem ? `${c.econItem.gfsCode} ${c.econItem.name}` : undefined,
    c.fundSource?.code,
  ].filter(Boolean);
  return parts.join(' · ');
}

interface RefData {
  votes: Vote[];
  fiscalYears: FiscalYear[];
  /** The OPEN fiscal year (latest if several). */
  fy: FiscalYear | null;
  loading: boolean;
  voteByCode: (code: string | undefined) => Vote | undefined;
  reload: () => void;
}

const RefDataContext = createContext<RefData | null>(null);

export function RefDataProvider({ children }: { children: ReactNode }) {
  const votesQ = useApiQuery<{ items: Vote[] }>('/api/v1/coa/votes?limit=100');
  const fyQ = useApiQuery<{ items: FiscalYear[] }>('/api/v1/coa/fiscal-years');

  const value = useMemo<RefData>(() => {
    const fiscalYears = fyQ.data?.items ?? [];
    const open = fiscalYears.filter((f) => f.status === 'OPEN').sort((a, b) => b.year - a.year);
    return {
      votes: votesQ.data?.items ?? [],
      fiscalYears,
      fy: open[0] ?? null,
      loading: votesQ.loading || fyQ.loading,
      voteByCode: (code) => (code ? (votesQ.data?.items ?? []).find((v) => v.code === code) : undefined),
      reload: () => {
        votesQ.reload();
        fyQ.reload();
      },
    };
  }, [votesQ.data, fyQ.data, votesQ.loading, fyQ.loading]);

  return <RefDataContext.Provider value={value}>{children}</RefDataContext.Provider>;
}

export function useRefData(): RefData {
  const ctx = useContext(RefDataContext);
  if (!ctx) throw new Error('useRefData outside RefDataProvider');
  return ctx;
}
